import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { initYandexMetrika } from './utils/yandexMetrika';
import Header from './components/Header';
import Footer from './components/Footer';

import Main from './pages/Main';
import About from './pages/About';
import Psychologist from './pages/Psychologists';
import PsychologistProfile from './pages/PsychologistProfile';
import Sessions from './pages/Sessions';
import GroupSessionPage from './pages/GroupSessionPage';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Forbidden from './pages/Forbidden';
import Chat from './pages/Chat';
import Diary from './pages/Diary';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import Conferences from './pages/Conferences';
import ConferenceRoom from './pages/ConferenceRoom';
import ConferenceGuestRoom from './pages/ConferenceGuestRoom';
import Subscription from './pages/Subscription';
import Finance from './pages/Finance';
import PsychologistCalendar from './pages/PsychologistCalendar';
import PsychologistClients from './pages/PsychologistClients';
import PsychologistNotes from './pages/PsychologistNotes';
import PsychologistTests from './pages/PsychologistTests';
import PsychologistGroupSessions from './pages/PsychologistGroupSessions';
import PsychologistTestBuilder from './pages/PsychologistTestBuilder';
import PsychologistPodcastBuilder from './pages/PsychologistPodcastBuilder';
import PsychologistPodcastEpisodeBuilder from './pages/PsychologistPodcastEpisodeBuilder';
import LiteratureBookEditor from './pages/LiteratureBookEditor';
import Literature from './pages/Literature';
import LiteratureBookDetails from './pages/LiteratureBookDetails';

import ProtectedRoute from './auth/ProtectedRoute';
import { useAuth } from "./auth/authStore";
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogPostEditor from './pages/BlogPostEditor';
import MyBlog from './pages/MyBlog';
import Podcasts from './pages/Podcasts';
import PodcastDetails from './pages/PodcastDetails';
import PodcastEpisode from './pages/PodcastEpisode';
import MyPodcasts from './pages/MyPodcasts';
import Pricing from './pages/Pricing';
import LegalDocument from './pages/LegalDocument';
import Forum from './pages/Forum';
import GlobalPodcastPlayer from './components/podcasts/GlobalPodcastPlayer';

import NotFound from './pages/NotFound';

import './scss/app.scss';
import {Helmet} from "react-helmet-async";
import {
  applyMaintenanceBannerPayload,
  getMaintenanceBannerState,
} from "./utils/maintenanceBanner";
import { siteBannerApi } from "./api/siteBannerApi";
import { psychologistsApi } from "./api/psychologistsApi";
import { FINANCE_SECTION_ENABLED } from "./config/env";

const isReactSnap = typeof navigator !== "undefined" && /ReactSnap/i.test(String(navigator.userAgent || ""));
const INVITE_ATTR_KEY = "bs:invite:psychologist";

function parsePsychologistIdToken(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const token = raw.split("-")[0];
  const id = Number(token);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function readEnvCra(key, fallback = '') {
  // В CRA process.env подменяется сборщиком на этапе билдa.
  // В рантайме в браузере "process" может отсутствовать — поэтому страховка.
  const env = typeof process !== 'undefined' && process?.env ? process.env : {};
  return String(env[key] || fallback || '');
}

function ensureAbsUrl(url, base) {
  if (!url) return '';
  const s = String(url).trim();
  if (!s) return '';
  // Уже абсолютный — возвращаем как есть
  if (/^https?:\/\//i.test(s)) return s;
  // Иначе делаем абсолютным от base (важно для react-snap,
  // чтобы в статике не появлялся localhost).
  const fallbackBase = base || 'https://burosoul.ru';
  try {
    return new URL(s, fallbackBase).toString();
  } catch {
    return '';
  }
}

function isLocalHost(hostname = '') {
  const h = String(hostname).toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h.endsWith('.local');
}

/**
 * Protect canonical/og:url from accidentally becoming localhost in production.
 *
 * This commonly happens when prerender tools (react-snap/puppeteer) run on
 * http://localhost:* and REACT_APP_SITE_URL is left as localhost by environment.
 */
function normalizeSiteOrigin(origin, fallback = 'https://burosoul.ru') {
  const raw = String(origin || '').trim();
  const isProd = readEnvCra('NODE_ENV', 'production') === 'production';
  if (!raw) return fallback;
  try {
    const u = new URL(raw);
    if (isProd && isLocalHost(u.hostname)) return fallback;
    return u.origin;
  } catch {
    try {
      const u = new URL(raw, fallback);
      if (isProd && isLocalHost(u.hostname)) return fallback;
      return u.origin;
    } catch {
      return fallback;
    }
  }
}

function useYandexMetrikaHit(counterId) {
    const location = useLocation();

    useEffect(() => {
        const url = location.pathname + location.search + location.hash;

        if (typeof window !== "undefined" && typeof window.ym === "function") {
            window.ym(counterId, "hit", url, { referer: document.referrer });
        }
    }, [location, counterId]);
}

const SITE_BANNER_DISMISS_KEY = "site-banner:dismissed-version";

function toBannerVersion(state) {
  const src = state && typeof state === "object" ? state : {};
  const explicitVersion = String(src.version || "").trim();
  if (explicitVersion) {
    return `v:${explicitVersion}`;
  }
  return JSON.stringify({
    enabled: Boolean(src.enabled),
    title: String(src.title || "").trim(),
    message: String(src.message || "").trim(),
    note: String(src.note || "").trim(),
    severity: String(src.severity || "warning").trim().toLowerCase(),
    siteEnabled: Boolean(src.siteEnabled),
    holidayStyleActive: Boolean(src.holidayStyleActive),
  });
}

function readDismissedBannerVersion() {
  if (typeof window === "undefined") return "";
  try {
    return String(window.localStorage.getItem(SITE_BANNER_DISMISS_KEY) || "");
  } catch {
    return "";
  }
}

function persistDismissedBannerVersion(version) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SITE_BANNER_DISMISS_KEY, String(version || ""));
  } catch {
    // ignore
  }
}

function AppSeo() {
  const location = useLocation();

  const SITE_NAME = readEnvCra('REACT_APP_SITE_NAME', 'Бюро Души');
  // Важно: если SITE_URL пустой, react-snap пререндерит разметку с localhost.
  // Поэтому даем стабильный дефолт для продакшена.
  const SITE_URL = readEnvCra('REACT_APP_SITE_URL', 'https://burosoul.ru');
  const DEFAULT_DESCRIPTION = readEnvCra(
    'REACT_APP_SITE_DESCRIPTION',
    'Подбор психолога под ваш запрос и удобная запись онлайн. Индивидуальные консультации и групповые сессии. Бережно, конфиденциально, с поддержкой на старте.'
  );
  // OG-картинка должна быть задана всегда, иначе валидаторы ругаются.
  // Используем стандартную CRA-иконку как безопасный дефолт.
  const DEFAULT_OG_IMAGE = readEnvCra('REACT_APP_OG_IMAGE', `${SITE_URL.replace(/\/+$/, '')}/logo512.png`);

  const TWITTER_SITE = readEnvCra('REACT_APP_TWITTER_SITE', ''); // @username
  const ORG_LOGO = readEnvCra('REACT_APP_ORG_LOGO', ''); // абсолютный url
  const SAME_AS_RAW = readEnvCra('REACT_APP_SAME_AS', ''); // "https://t.me/..,https://vk.com/.."

  const siteOrigin = useMemo(() => {
    const fromEnv = (SITE_URL || '').trim();
    const fromWindow = typeof window !== 'undefined' ? window.location.origin : '';
    return normalizeSiteOrigin(fromEnv || fromWindow, 'https://burosoul.ru');
  }, [SITE_URL]);

  const canonicalUrl = useMemo(() => {
    if (!siteOrigin) return '';
    const pathname = location?.pathname || '/';
    return new URL(pathname, siteOrigin).toString();
  }, [siteOrigin, location?.pathname]);

  const ogImage = useMemo(() => {
    const v = DEFAULT_OG_IMAGE ? ensureAbsUrl(DEFAULT_OG_IMAGE, siteOrigin || 'https://burosoul.ru') : '';
    return v || '';
  }, [DEFAULT_OG_IMAGE, siteOrigin]);

  const sameAs = useMemo(() => {
    if (!SAME_AS_RAW) return [];
    return SAME_AS_RAW.split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [SAME_AS_RAW]);

  const orgSchema = useMemo(() => {
    if (!siteOrigin) return null;
    const obj = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: siteOrigin,
    };
    if (ORG_LOGO) obj.logo = ensureAbsUrl(ORG_LOGO) || ORG_LOGO;
    if (sameAs.length) obj.sameAs = sameAs;
    return obj;
  }, [SITE_NAME, siteOrigin, ORG_LOGO, sameAs]);

  const websiteSchema = useMemo(() => {
    if (!siteOrigin) return null;
    const searchTarget = new URL('/blog?query={search_term_string}', siteOrigin).toString();

    return {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: siteOrigin,
      potentialAction: {
        '@type': 'SearchAction',
        target: searchTarget,
      },
    };
  }, [SITE_NAME, siteOrigin]);

  const webPageSchema = useMemo(() => {
    if (!canonicalUrl) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: SITE_NAME,
      url: canonicalUrl,
      inLanguage: 'ru-RU',
    };
  }, [SITE_NAME, canonicalUrl]);

  return (
    <Helmet prioritizeSeoTags>
      {/* База */}
      <html lang="ru" />
      <title>{SITE_NAME}</title>

      {/* Favicon: жёстко указываем публичные ассеты, чтобы не было fallback
          на CRA favicon.ico или иных кэшированных иконок браузера. */}
      <link rel="icon" href="/icons/pwa-icon-any.svg" type="image/svg+xml" />
      <link rel="icon" href="/icons/icon-192.png" sizes="192x192" type="image/png" />
      <link rel="shortcut icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.webmanifest" />
      <meta name="theme-color" content="#085041" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      <meta
        name="description"
        content={DEFAULT_DESCRIPTION}
      />
      {canonicalUrl ? (
        <link
          rel="canonical"
          href={canonicalUrl}
        />
      ) : null}
      <meta
        name="robots"
        content="index, follow"
      />
      <meta name="yandex-verification" content="f3e16406e43ccf08" />

      {/* Open Graph */}
      <meta
        property="og:site_name"
        content={SITE_NAME}
      />
      <meta
        property="og:type"
        content="website"
      />
      <meta
        property="og:locale"
        content="ru_RU"
      />
      <meta
        property="og:title"
        content={SITE_NAME}
      />
      <meta
        property="og:description"
        content={DEFAULT_DESCRIPTION}
      />
      {canonicalUrl ? (
        <meta
          property="og:url"
          content={canonicalUrl}
        />
      ) : null}
      {ogImage ? (
        <meta
          property="og:image"
          content={ogImage}
        />
      ) : null}
      {ogImage ? (
        <meta
          property="og:image:alt"
          content={SITE_NAME}
        />
      ) : null}

      {/* Twitter */}

      <meta
        name="twitter:card"
        content={ogImage ? 'summary_large_image' : 'summary'}
      />
      {TWITTER_SITE ? (
        <meta
          name="twitter:site"
          content={TWITTER_SITE}
        />
      ) : null}
      <meta
        name="twitter:title"
        content={SITE_NAME}
      />
      <meta
        name="twitter:description"
        content={DEFAULT_DESCRIPTION}
      />
      {ogImage ? (
        <meta
          name="twitter:image"
          content={ogImage}
        />
      ) : null}

      {/* JSON-LD: базовые схемы */}
      {orgSchema ? <script type="application/ld+json">{JSON.stringify(orgSchema)}</script> : null}
      {websiteSchema ? <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script> : null}
      {webPageSchema ? <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script> : null}
    </Helmet>
  );
}

function App() {
  const counterId = Number(process.env.REACT_APP_YM_COUNTER_ID || 106839128);
  const [siteBanner, setSiteBanner] = useState(() => getMaintenanceBannerState());
  const [bannerVersion, setBannerVersion] = useState(() => toBannerVersion(getMaintenanceBannerState()));
  const [dismissedBannerVersion, setDismissedBannerVersion] = useState(readDismissedBannerVersion);
  const [maintenanceBannerOffset, setMaintenanceBannerOffset] = useState(0);
  const maintenanceBannerRef = React.useRef(null);

  useEffect(() => {
    initYandexMetrika(counterId);
  }, [counterId]);

  useYandexMetrikaHit(counterId);

  useEffect(() => {
    if (isReactSnap) return undefined;

    let alive = true;
    siteBannerApi
      .getPublicBanner()
      .then((payload) => {
        if (!alive || !payload) return;
        const nextBanner = applyMaintenanceBannerPayload(payload);
        setSiteBanner(nextBanner);
        setBannerVersion(toBannerVersion(nextBanner));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const pluralMatch = location.pathname.match(/^\/psychologists\/([^/]+)\/?$/i);
    if (pluralMatch) {
      const aliasToken = String(pluralMatch[1] || "").trim();
      if (!aliasToken) {
        navigate(`/psychologist${location.search || ""}${location.hash || ""}`, { replace: true });
        return;
      }
      const aliasId = parsePsychologistIdToken(aliasToken);
      const normalizedToken = aliasId ? String(aliasId) : aliasToken;
      navigate(`/psychologist/${encodeURIComponent(normalizedToken)}${location.search || ""}${location.hash || ""}`, {
        replace: true,
      });
      return;
    }

    if (location.pathname !== "/psychologist") return;
    const sp = new URLSearchParams(location.search || "");
    const psychologistIdRaw = String(sp.get("psychologistId") || "").trim();
    if (!psychologistIdRaw) return;
    const normalizedId = String(parsePsychologistIdToken(psychologistIdRaw) || "").trim();
    if (!normalizedId) return;
    sp.delete("psychologistId");
    const nextSearch = sp.toString();
    const nextPath = `/psychologist/${encodeURIComponent(normalizedId)}${nextSearch ? `?${nextSearch}` : ""}${
      location.hash || ""
    }`;
    navigate(nextPath, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    let cancelled = false;
    const match = location.pathname.match(/^\/psychologist\/([^/]+)\/?$/i);
    if (!match) return () => {
      cancelled = true;
    };
    const sp = new URLSearchParams(location.search || "");
    if (String(sp.get("source") || "").trim().toLowerCase() !== "invite") return;

    const saveInvite = (id) => {
      if (!id || cancelled) return;
      try {
        window.localStorage.setItem(
          INVITE_ATTR_KEY,
          JSON.stringify({
            psychologistId: id,
            source: "invite",
            capturedAt: new Date().toISOString(),
            path: location.pathname,
          })
        );
      } catch {
        // ignore
      }
    };

    const directId =
      parsePsychologistIdToken(sp.get("psychologistId")) ||
      parsePsychologistIdToken(match[1]);
    if (directId) {
      saveInvite(directId);
      return () => {
        cancelled = true;
      };
    }

    const routeToken = String(match[1] || "").trim();
    if (!routeToken) return () => {
      cancelled = true;
    };
    psychologistsApi
      .resolveProfile(routeToken)
      .then((raw) => {
        if (cancelled || !raw || typeof raw !== "object") return;
        const id = Number(raw.userId ?? raw.id ?? raw.user_id ?? raw.psychologistId ?? raw.psychologist_id);
        if (Number.isFinite(id) && id > 0) saveInvite(id);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search]);
  const isPsychologistProfileRoute = /^\/psychologist\/[^/]+\/?$/.test(location.pathname);
  const isPsychologistProfileShellRoute = isPsychologistProfileRoute && isAuthenticated;
  const isPsychologistFinderRoute = /^\/(catalog|psychologist|psychologists)\/?$/.test(location.pathname);
  const isDashboardHomeRoute = /^\/dashboard\/?$/.test(location.pathname);
  const isBlogEditorRoute = /^\/dashboard\/blog(?:\/new|\/[^/]+\/edit)\/?$/.test(location.pathname);
  const isDashboardBlogRoute = /^\/dashboard\/blog(?:\/.*)?$/.test(location.pathname);
  const isNotificationsRoute = location.pathname === "/notifications";
  const isChatRoute = location.pathname === "/chat";
  const isDashboardSettingsRoute = location.pathname === "/dashboard/settings";
  const isMyAppointmentsRoute = location.pathname === "/my-appointments";
  const isDashboardSubscriptionRoute = /^\/dashboard\/subscription(?:\/.*)?$/.test(location.pathname);
  const isDashboardFinanceRoute = FINANCE_SECTION_ENABLED && /^\/dashboard\/finance(?:\/.*)?$/.test(location.pathname);
  const isDashboardDiaryRoute = /^\/dashboard\/diary(?:\/.*)?$/.test(location.pathname);
  const isDashboardTestsRoute = /^\/dashboard\/tests(?:\/.*)?$/.test(location.pathname);
  const isDashboardPodcastsRoute = /^\/dashboard\/podcasts(?:\/.*)?$/.test(location.pathname);
  const isDashboardCalendarRoute = /^\/dashboard\/calendar(?:\/.*)?$/.test(location.pathname);
  const isDashboardClientsRoute = /^\/dashboard\/clients(?:\/.*)?$/.test(location.pathname);
  const isDashboardNotesRoute = /^\/dashboard\/notes(?:\/.*)?$/.test(location.pathname);
  const isDashboardGroupSessionsRoute = /^\/dashboard\/group-sessions(?:\/.*)?$/.test(location.pathname);
  const isDashboardConferencesRoute = /^\/dashboard\/conferences(?:\/.*)?$/.test(location.pathname);
  const isGuestConferenceRoute = /^\/conference(?:\/.*)?$/.test(location.pathname);
  const isLiteratureEditorRoute = /^\/dashboard\/literature\/new\/?$/.test(location.pathname);
  const isLiteratureRoute = /^\/literature(?:\/.*)?$/.test(location.pathname);
  const isPodcastsRoute = /^\/podcasts(?:\/.*)?$/.test(location.pathname);
  const isBlogRoute = /^\/blog(?:\/.*)?$/.test(location.pathname);
  const isForumRoute = /^\/forum(?:\/.*)?$/.test(location.pathname);
  const isAuthRoute = /^\/(login|auth|register|recovery|reset-password|forgot-password)(?:\/.*)?\/?$/i.test(
    location.pathname
  );

  // Скрываем Header только на странице авторизации.
  const hideHeader =
    location.pathname === '/login' ||
    isPsychologistFinderRoute ||
    isPsychologistProfileShellRoute ||
    isDashboardHomeRoute ||
    isNotificationsRoute ||
    isChatRoute ||
    isDashboardSettingsRoute ||
    isMyAppointmentsRoute ||
    isDashboardSubscriptionRoute ||
    isDashboardFinanceRoute ||
    isDashboardDiaryRoute ||
    isDashboardTestsRoute ||
    isDashboardPodcastsRoute ||
    isDashboardBlogRoute ||
    isDashboardCalendarRoute ||
    isDashboardClientsRoute ||
    isDashboardNotesRoute ||
    isDashboardGroupSessionsRoute ||
    isDashboardConferencesRoute ||
    isGuestConferenceRoute ||
    isLiteratureEditorRoute ||
    isLiteratureRoute ||
    isPodcastsRoute ||
    (isBlogRoute && isAuthenticated) ||
    (isForumRoute && isAuthenticated);

  // Footer также скрываем только на странице авторизации.
  const hideFooter =
    location.pathname === '/login' ||
    isPsychologistFinderRoute ||
    isPsychologistProfileShellRoute ||
    isDashboardHomeRoute ||
    isNotificationsRoute ||
    isChatRoute ||
    isDashboardSettingsRoute ||
    isMyAppointmentsRoute ||
    isDashboardSubscriptionRoute ||
    isDashboardFinanceRoute ||
    isDashboardDiaryRoute ||
    isDashboardTestsRoute ||
    isDashboardPodcastsRoute ||
    isDashboardBlogRoute ||
    isDashboardCalendarRoute ||
    isDashboardClientsRoute ||
    isDashboardNotesRoute ||
    isDashboardGroupSessionsRoute ||
    isDashboardConferencesRoute ||
    isGuestConferenceRoute ||
    isLiteratureEditorRoute ||
    isLiteratureRoute ||
    isPodcastsRoute ||
    (isBlogRoute && isAuthenticated) ||
    (isForumRoute && isAuthenticated);
  const hideMaintenanceBanner = isAuthRoute;
  const isMaintenanceBannerDismissed =
    Boolean(dismissedBannerVersion) && dismissedBannerVersion === bannerVersion;

  const showMaintenanceBanner =
    Boolean(siteBanner?.enabled || siteBanner?.siteEnabled === false) &&
    !hideMaintenanceBanner &&
    !isMaintenanceBannerDismissed;
  const maintenanceSeverityClass = `is-${siteBanner?.severity || "warning"}`;
  const maintenanceIcon = siteBanner?.severity === "critical" ? "⚠️" : siteBanner?.severity === "success" ? "✅" : "ℹ️";
  const maintenanceTitle = String(siteBanner?.title || "Внимание").trim();
  const maintenanceMessage = String(siteBanner?.message || "").trim();
  const maintenanceNote = String(siteBanner?.note || "").trim();
  const handleDismissMaintenanceBanner = () => {
    const currentVersion = String(bannerVersion || "").trim();
    if (!currentVersion) return;
    setDismissedBannerVersion(currentVersion);
    persistDismissedBannerVersion(currentVersion);
  };

  useEffect(() => {
    if (!showMaintenanceBanner) {
      setMaintenanceBannerOffset(0);
      return undefined;
    }

    const bannerNode = maintenanceBannerRef.current;
    if (!bannerNode) {
      setMaintenanceBannerOffset(0);
      return undefined;
    }

    const updateOffset = () => {
      const nextOffset = Math.round(bannerNode.offsetHeight || bannerNode.getBoundingClientRect().height || 0);
      setMaintenanceBannerOffset((prev) => (prev === nextOffset ? prev : nextOffset));
    };

    updateOffset();

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(() => updateOffset());
      resizeObserver.observe(bannerNode);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener("resize", updateOffset);
    return () => window.removeEventListener("resize", updateOffset);
  }, [showMaintenanceBanner, bannerVersion]);

  return (
    <div className="App" style={{ "--site-maintenance-offset": `${maintenanceBannerOffset}px` }}>
      <AppSeo />
      {showMaintenanceBanner ? (
        <div
          ref={maintenanceBannerRef}
          id="site-maintenance-banner"
          className={`site-maintenance-banner ${maintenanceSeverityClass}${siteBanner?.holidayStyleActive ? " is-holiday" : ""}`}
          role="status"
          aria-live="polite"
        >
          <div className="site-maintenance-banner__inner">
            <span className="site-maintenance-banner__icon" aria-hidden="true">{maintenanceIcon}</span>
            <div className="site-maintenance-banner__content">
              {maintenanceTitle ? <strong className="site-maintenance-banner__title">{maintenanceTitle}</strong> : null}
              {maintenanceMessage ? <span className="site-maintenance-banner__message">{maintenanceMessage}</span> : null}
              {maintenanceNote ? <span className="site-maintenance-banner__note">{maintenanceNote}</span> : null}
            </div>
            <button
              type="button"
              className="site-maintenance-banner__dismiss"
              onClick={handleDismissMaintenanceBanner}
              aria-label="Скрыть информационный баннер"
            >
              ✕
            </button>
          </div>
        </div>
      ) : null}

      {!hideHeader && <Header />}

      <div
        className={`wrapper${
          isPsychologistProfileShellRoute ||
          isPsychologistFinderRoute ||
          isDashboardHomeRoute ||
          isBlogEditorRoute ||
          isNotificationsRoute ||
          isChatRoute ||
          isDashboardSettingsRoute ||
          isMyAppointmentsRoute ||
          isDashboardSubscriptionRoute ||
          isDashboardFinanceRoute ||
          isDashboardDiaryRoute ||
          isDashboardTestsRoute ||
          isDashboardPodcastsRoute ||
          isDashboardBlogRoute ||
          isDashboardCalendarRoute ||
          isDashboardClientsRoute ||
          isDashboardNotesRoute ||
          isDashboardGroupSessionsRoute ||
          isDashboardConferencesRoute ||
          isLiteratureEditorRoute ||
          isLiteratureRoute ||
          isPodcastsRoute ||
          isGuestConferenceRoute ||
          (isBlogRoute && isAuthenticated) ||
          (isForumRoute && isAuthenticated)
            ? " wrapper--plain"
            : ""
        }`}
      >
        <Routes>
          <Route
            path="/"
            element={<Main />}
          />
          <Route
            path="/about"
            element={<About />}
          />
          <Route
            path="/psychologist/:id"
            element={<PsychologistProfile />}
          />
          <Route
            path="/psychologists/:id"
            element={<PsychologistProfile />}
          />
          {/* Поддержка ЧПУ: /psychologist/14-ivanov-ivan-ivanovich */}
          <Route
            path="/psychologist/:id-:slug"
            element={<PsychologistProfile />}
          />
          <Route
            path="/psychologists/:id-:slug"
            element={<PsychologistProfile />}
          />
          <Route
            path="/sessions"
            element={<Sessions />}
          />
          <Route
            path="/group-sessions/:id"
            element={<GroupSessionPage />}
          />
          <Route
            path="/login"
            element={<Login />}
          />
          <Route
            path="/403"
            element={<Forbidden />}
          />

          <Route
            path="/blog"
            element={<Blog />}
          />
          <Route
            path="/blog/:slug"
            element={<BlogPost />}
          />
          <Route
            path="/podcasts"
            element={<Podcasts />}
          />
          <Route
            path="/podcasts/:podcastId"
            element={<PodcastDetails />}
          />
          <Route
            path="/podcasts/episodes/:episodeId"
            element={<PodcastEpisode />}
          />
          <Route
            path="/literature"
            element={<Literature />}
          />
          <Route
            path="/literature/books/:bookIdOrSlug"
            element={<LiteratureBookDetails />}
          />
          <Route
            path="/pricing"
            element={<Pricing />}
          />
          <Route
            path="/documents/:docSlug"
            element={<LegalDocument />}
          />
          <Route
            path="/docs/:docSlug"
            element={<LegalDocument />}
          />
          <Route
            path="/legal/:docSlug"
            element={<LegalDocument />}
          />
          <Route
            path="/forum"
            element={<Forum />}
          />
          <Route
            path="/forum/sections/:sectionId"
            element={<Forum />}
          />
          <Route
            path="/forum/topics/:topicId"
            element={<Forum />}
          />
          <Route
            path="/forum/my/:meTab"
            element={<Forum />}
          />
          <Route
            path="/conference/:roomToken"
            element={<ConferenceGuestRoom />}
          />
          <Route
            path="/conference/room/:roomToken"
            element={<ConferenceGuestRoom />}
          />
          <Route
            path="/conference/*"
            element={<ConferenceGuestRoom />}
          />

          <Route element={<ProtectedRoute roles={['CLIENT', 'PSYCHOLOGIST', 'PSYCHO', 'ADMIN', 'SUPER_ADMIN', 'MODERATOR']} />}>
            <Route
              path="/catalog"
              element={<Psychologist />}
            />
            <Route
              path="/psychologist"
              element={<Psychologist />}
            />
            <Route
              path="/psychologists"
              element={<Psychologist />}
            />
            <Route
              path="/my-appointments"
              element={<Navigate to="/dashboard/settings?tab=appointments" replace />}
            />
            <Route
              path="/dashboard"
              element={<Dashboard />}
            />
            <Route
              path="/dashboard/settings"
              element={<Settings />}
            />
            <Route
              path="/dashboard/conferences"
              element={<Conferences />}
            />
            <Route
              path="/dashboard/conferences/room/:roomToken"
              element={<ConferenceRoom />}
            />
            <Route
              path="/dashboard/conferences/:bookingId"
              element={<ConferenceRoom />}
            />
            <Route
              path="/dashboard/group-sessions"
              element={<PsychologistGroupSessions />}
            />
            <Route
              path="/dashboard/group-sessions/:id"
              element={<PsychologistGroupSessions />}
            />
            <Route
              path="/dashboard/group-sessions/my"
              element={<PsychologistGroupSessions />}
            />
            <Route
              path="/dashboard/group-sessions/my/:id"
              element={<PsychologistGroupSessions />}
            />
            <Route
              path="/dashboard/subscription"
              element={<Subscription />}
            />
            <Route
              path="/dashboard/finance"
              element={<ProtectedRoute roles={['PSYCHOLOGIST', 'PSYCHO', 'ADMIN', 'SUPER_ADMIN', 'MODERATOR']} />}
            >
              <Route
                index
                element={FINANCE_SECTION_ENABLED ? <Finance /> : <Navigate to="/dashboard" replace />}
              />
            </Route>
          <Route
            path="/dashboard/diary"
            element={<ProtectedRoute roles={['CLIENT']} />}
          >
            <Route
              index
              element={<Diary />}
            />
          </Route>
            <Route
              path="/chat"
              element={<Chat />}
            />
            <Route
              path="/notifications"
              element={<Notifications />}
            />
          </Route>

          <Route element={<ProtectedRoute roles={['PSYCHOLOGIST', 'PSYCHO', 'ADMIN', 'SUPER_ADMIN', 'MODERATOR']} />}>
            <Route
              path="/dashboard/calendar"
              element={<PsychologistCalendar />}
            />
            <Route
              path="/dashboard/tests"
              element={<PsychologistTests />}
            />
            <Route
              path="/dashboard/tests/new"
              element={<PsychologistTestBuilder />}
            />
            <Route
              path="/dashboard/podcasts"
              element={<MyPodcasts />}
            />
            <Route
              path="/dashboard/podcasts/new"
              element={<PsychologistPodcastBuilder />}
            />
            <Route
              path="/dashboard/podcasts/:podcastId/edit"
              element={<PsychologistPodcastBuilder />}
            />
            <Route
              path="/dashboard/podcasts/:podcastId/episodes/new"
              element={<PsychologistPodcastEpisodeBuilder />}
            />
            <Route
              path="/dashboard/podcasts/:podcastId/episodes/:episodeId/edit"
              element={<PsychologistPodcastEpisodeBuilder />}
            />
            <Route
              path="/dashboard/blog"
              element={<MyBlog />}
            />
            <Route
              path="/dashboard/blog/new"
              element={<BlogPostEditor />}
            />
            <Route
              path="/dashboard/blog/:postId/edit"
              element={<BlogPostEditor />}
            />
            <Route
              path="/dashboard/literature/new"
              element={<LiteratureBookEditor />}
            />
          </Route>

          <Route element={<ProtectedRoute roles={['PSYCHOLOGIST', 'PSYCHO']} />}>
            <Route
              path="/dashboard/clients"
              element={<PsychologistClients />}
            />
            <Route
              path="/dashboard/notes"
              element={<PsychologistNotes />}
            />
          </Route>

          <Route
            path="*"
            element={<NotFound />}
          />
        </Routes>
      </div>

      <GlobalPodcastPlayer />
      {!hideFooter && <Footer />}
    </div>
  );
}

export default App;
